import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import StatusTrail, { BusinessStatusBadge } from "../components/StatusTrail";
import { GeneralStatusBadge } from "../components/GeneralStatusBadge";
import { GeneralStatusExplainer } from "../components/GeneralStatusExplainer";
import { ContractDocuments } from "../components/ContractDocuments";
import CommercialOwnershipPanel from "../components/CommercialOwnershipPanel";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { CopyableValue } from "../components/CopyableValue";
// Iter53 P32 — Extras components for cleaner SaleDetail.js
import ReworkBanner from "../components/SaleDetail/ReworkBanner";
import ReworkResolveDialog from "../components/SaleDetail/ReworkResolveDialog";
// Iter53 P33 — Mobile sheets extras
import { MobileTabSheet, MobileQuickInfoSheet } from "../components/SaleDetail/MobileSheets";
import SupplierFieldsEditDialog from "../components/SaleDetail/SupplierFieldsEditDialog";
import { EmptyValue } from "../components/EmptyValue";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { useFieldAccess, FlacScopeBanner } from "../hooks/useFieldAccess";
import SaleOverview from "../components/SaleOverview";
import ActivityFeed from "../components/ActivityFeed";
import { formatRoDate } from "../lib/dates";
import {
  ArrowLeft,
  Edit,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  User,
  Building2,
  MapPin,
  FileText,
  History,
  Upload,
  Download,
  RefreshCw,
  AlertCircle,
  Package,
  Zap,
  Database,
  UserCheck,
  AlertTriangle,
  ChevronDown,
  Check
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Iter53 P22 — Mobile tab selector options (label per tab value)
const TAB_OPTIONS = [
  { value: "overview", label: "Overview" },
  { value: "details", label: "Detalii" },
  { value: "client", label: "Client" },
  { value: "contract", label: "Contract" },
  { value: "salesforce", label: "Salesforce / Workflow" },
  { value: "documents", label: "Documente" },
  { value: "history", label: "Istoric" },
  { value: "activity", label: "Activitate" },
  { value: "audit", label: "Tehnice & Audit" },
];

// P0.6 — Badge „sursa ultimei modificări" a unui câmp (ex. status business).
// NU reprezintă sursa importului (aceea e sale.import_source) — eticheta o spune explicit.
const SOURCE_LABELS = { salesforce: "SF", manual: "manual", easy: "Easy", crm: "CRM Legacy", crm_legacy: "CRM Legacy", legacy: "CRM Legacy" };
const FieldSourceBadge = ({ source, setAt, prevSource, dataTestid, fieldLabel = "status business" }) => {
  if (!source) {
    return (
      <span
        className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500 border border-slate-200 cursor-help"
        title={`Sursa ultimei modificări a câmpului „${fieldLabel}" nu este înregistrată (valoare implicită la creare). Nu este sursa importului — vezi „Sursă import" în Tehnice & Audit.`}
        data-testid={dataTestid}
      >
        sursă {fieldLabel}: neînregistrată
      </span>
    );
  }
  const isSF = source === "salesforce";
  const isManual = source === "manual";
  const cls = isSF
    ? "bg-blue-50 text-blue-700 border-blue-200"
    : isManual
    ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-slate-100 text-slate-600 border-slate-200";
  const label = SOURCE_LABELS[source] || source;
  const Icon = isSF ? Database : UserCheck;
  const tooltipParts = [`Sursa ultimei modificări „${fieldLabel}": ${label}`];
  if (setAt) tooltipParts.push(`Setat: ${new Date(setAt).toLocaleString("ro-RO")}`);
  if (prevSource) tooltipParts.push(`Sursă anterioară: ${SOURCE_LABELS[prevSource] || prevSource}`);
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border cursor-help ${cls}`}
      title={tooltipParts.join("\n")}
      data-testid={dataTestid}
    >
      <Icon className="h-2.5 w-2.5" />
      sursă {fieldLabel}: {label}
    </span>
  );
};

// Returns minutes since `set_at` or null
const minutesSince = (iso) => {
  if (!iso) return null;
  try {
    return (Date.now() - new Date(iso).getTime()) / 60000;
  } catch {
    return null;
  }
};

// Iter52 P14q — inline editor pentru `notes_bo` (Note backoffice).
// Editare directă din pagina principală a vânzării, fără modal.
const NotesInlineEditor = ({ initialValue, saleId, onSaved }) => {
  const [value, setValue] = React.useState(initialValue);
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/sales/${saleId}`, { notes_bo: value || null });
      toast.success("Note salvate");
      setEditing(false);
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare note");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div
        className="mt-1 text-sm text-slate-900 whitespace-pre-wrap rounded-md border border-dashed border-slate-200 p-2 cursor-pointer hover:border-indigo-300 hover:bg-slate-50 transition"
        onClick={() => setEditing(true)}
        data-testid="notes-bo-readonly"
        title="Click pentru editare"
      >
        {value
          ? value
          : <span className="text-slate-400 italic">— fără note (click pentru a adăuga) —</span>}
      </div>
    );
  }
  return (
    <div className="mt-1 space-y-2">
      <textarea
        className="w-full border rounded-md p-2 text-sm font-sans"
        rows={4}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoFocus
        data-testid="notes-bo-editor"
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={save}
          disabled={saving}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="notes-bo-save"
        >
          {saving ? "Se salvează..." : "Salvează note"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => { setValue(initialValue); setEditing(false); }}
          data-testid="notes-bo-cancel"
        >
          Anulează
        </Button>
      </div>
    </div>
  );
};


// =================================================================
// Iter52 P14x — SfStatusCluster
// Trei badge-uri compacte lângă POD pentru "stare reală la o privire":
//   - SF Synced ✓ (cu data ultimei sincronizări).
//   - QC: Pending / OK / KO (Quality Call PPC).
//   - Match: NN% (încredere reconciliere agent).
// =================================================================
const SfStatusCluster = ({ sale }) => {
  const c = sale?.contract || {};
  const sfSynced = !!(c.sf_contract_id || sale.sf_id || c.last_synced_from_sf_at);
  const lastSync = c.last_synced_from_sf_at;
  // Iter64e — QC PPC: aceeași sursă ca cardul „QC PPC (Salesforce)" din Overview
  // (contract_status.quality_call_outcome, fallback sale.quality_call_outcome).
  // Înainte citea `contract.quality_call_*` (câmp inexistent) → afișa mereu „neefectuat".
  const cs = sale?.contract_status || {};
  const qcOutcome = cs.quality_call_outcome || sale?.quality_call_outcome || null;
  const qcDate = cs.quality_call_date || null;
  const matchConf = sale.agent_match_confidence;

  // Map QC outcome to label/tone
  let qcLabel = "neînregistrat";
  let qcClass = "bg-slate-50 text-slate-500 border-slate-200";
  if (qcOutcome) {
    const low = String(qcOutcome).toLowerCase();
    if (low === "ok") {
      qcLabel = "OK";
      qcClass = "bg-emerald-50 text-emerald-700 border-emerald-200";
    } else if (low.startsWith("ko") || low === "respins") {
      qcLabel = "KO";
      qcClass = "bg-red-50 text-red-700 border-red-200";
    } else {
      qcLabel = String(qcOutcome).slice(0, 20);
      qcClass = "bg-slate-50 text-slate-700 border-slate-200";
    }
  }

  const formatPercent = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return `${Math.round(n * 100)}%`;
  };
  const matchPct = formatPercent(matchConf);
  let matchClass = "bg-slate-50 text-slate-700 border-slate-200";
  if (matchPct !== null && matchConf !== null && matchConf !== undefined) {
    if (matchConf >= 0.9) matchClass = "bg-emerald-50 text-emerald-700 border-emerald-200";
    else if (matchConf >= 0.7) matchClass = "bg-amber-50 text-amber-800 border-amber-200";
    else matchClass = "bg-red-50 text-red-700 border-red-200";
  }

  // Iter69h — QC AQS (DISTINCT de QC PPC). Sursă: sale.aqs_qc (tabel dedicat aqs_qc_results).
  const aqsQc = sale?.aqs_qc || null;
  let aqsQcLabel = null;
  let aqsQcClass = "bg-slate-50 text-slate-700 border-slate-200";
  let aqsQcTitle = "";
  if (aqsQc) {
    const n = aqsQc.outcome_normalized;
    if (n === "qc_ok") { aqsQcLabel = "OK"; aqsQcClass = "bg-emerald-50 text-emerald-700 border-emerald-200"; }
    else if (n === "qc_ko") { aqsQcLabel = "KO"; aqsQcClass = "bg-red-50 text-red-700 border-red-200"; }
    else { aqsQcLabel = "Necunoscut"; aqsQcClass = "bg-amber-50 text-amber-800 border-amber-200"; }
    const op = aqsQc.operator_source ? `Responsabil în sursă: ${aqsQc.operator_source}` : "Responsabil în sursă: neprecizat";
    const dt = aqsQc.call_date ? `data apelului: ${formatRoDate(aqsQc.call_date)}` : "data apelului: neînregistrată";
    aqsQcTitle = `QC AQS (distinct de QC PPC) — rezultat brut „${aqsQc.outcome_raw || "—"}" / status „${aqsQc.status_raw || "—"}". ${op}. ${dt}. Sursă: ${aqsQc.source} · dosar ${aqsQc.dossier_uuid} · rev ${aqsQc.dossier_revision}. «sent» ≠ QC pozitiv.`;
  }

  return (
    <>
      {sfSynced && (
        <Badge
          variant="outline"
          className="flex items-center gap-1 bg-emerald-50 text-emerald-700 border-emerald-200"
          title={lastSync ? `Ultima sincronizare SF: ${new Date(lastSync).toLocaleString("ro-RO")}` : "Sincronizat cu Salesforce"}
          data-testid="badge-sf-synced"
        >
          <CheckCircle2 className="h-3 w-3" />
          SF Synced
        </Badge>
      )}
      <Badge
        variant="outline"
        className={`flex items-center gap-1 ${qcClass}`}
        title={qcOutcome
          ? `Quality Call PPC — rezultat: „${qcOutcome}"${qcDate ? ` · data: ${formatRoDate(qcDate)}` : " · data apelului: neînregistrată"}`
          : "Quality Call PPC — niciun rezultat înregistrat (nu înseamnă că apelul nu a fost efectuat)."}
        data-testid="badge-qc-status"
      >
        QC PPC: {qcLabel}
      </Badge>
      {aqsQcLabel && (
        <Badge
          variant="outline"
          className={`flex items-center gap-1 ${aqsQcClass}`}
          title={aqsQcTitle}
          data-testid="badge-qc-aqs"
        >
          QC AQS: {aqsQcLabel}
        </Badge>
      )}
      {matchPct !== null && (
        <Badge
          variant="outline"
          className={`flex items-center gap-1 ${matchClass}`}
          title="Încredere reconciliere agent (raw → CRM)"
          data-testid="badge-agent-match"
        >
          Match: {matchPct}
        </Badge>
      )}
    </>
  );
};



const SaleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasRole, canEdit, canDelete } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "overview";
  const setActiveTab = (t) => {
    const next = new URLSearchParams(searchParams);
    if (t === "overview") next.delete("tab"); else next.set("tab", t);
    setSearchParams(next, { replace: true });
  };
  
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter39 — FLAC scope (hidden / read-only fields per role)
  const flac = useFieldAccess(sale);
  const [statuses, setStatuses] = useState([]);
  const [processingCases, setProcessingCases] = useState([]); // Iter35
  const [fieldSources, setFieldSources] = useState({});  // P0.6 — { business_status: { source, set_at, previous_source, set_by_user_id } }
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);
  // Iter31 P3b — agents cache for commercial ownership panel
  const [agents, setAgents] = useState([]);
  
  const [statusDialog, setStatusDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [supplierEditDialog, setSupplierEditDialog] = useState(false);
  const [supplierForm, setSupplierForm] = useState({});
  
  const [newStatus, setNewStatus] = useState("");
  const [uploading, setUploading] = useState(false);
  // Iter53 P22 — Mobile UI state
  const [showTabSheet, setShowTabSheet] = useState(false);
  const [showQuickInfoMobile, setShowQuickInfoMobile] = useState(false);
  // Iter53 P28 — Rework resolve dialog (admin/BO)
  const [reworkResolveDialog, setReworkResolveDialog] = useState(false);
  const [reworkResolveNote, setReworkResolveNote] = useState("");
  const [reworkResolving, setReworkResolving] = useState(false);

  const handleResolveRework = async () => {
    if (!reworkResolveNote.trim()) {
      toast.error("Adaugă o notă de rezolvare (obligatorie pentru audit).");
      return;
    }
    setReworkResolving(true);
    try {
      const note = reworkResolveNote.trim();
      // 1. Append resolution note to notes_bo for trail
      const prefix = sale.notes_bo ? `${sale.notes_bo}\n\n` : "";
      const stamp = new Date().toLocaleString("ro-RO");
      const newNotes = `${prefix}[REWORK REZOLVAT · ${stamp} · ${user.name || user.email}]\n${note}`;
      await axios.put(`${API_URL}/sales/${id}`, { notes_bo: newNotes });
      // 2. Clear rework flag
      await axios.post(`${API_URL}/sales/${id}/clear-rework`, { note });
      toast.success("Rework rezolvat. Vânzarea a fost retrimisă spre reprocesare BO.");
      setReworkResolveDialog(false);
      setReworkResolveNote("");
      await fetchSale();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la rezolvarea rework");
    } finally {
      setReworkResolving(false);
    }
  };

  const fetchSale = async () => {
    try {
      const response = await axios.get(`${API_URL}/sales/${id}`);
      setSale(response.data);
      // P0.6 — fetch field-sources to drive SF / manual badges
      const cId = response.data?.contract_id || response.data?.contract?.id;
      if (cId) {
        // Iter54 P1 perf — cele 2 fetch-uri rulează în PARALEL (erau secvențiale)
        const [fsResp, pcResp] = await Promise.allSettled([
          axios.get(`${API_URL}/contracts/${cId}/field-sources`),
          axios.get(`${API_URL}/contracts/${cId}/processing-cases`),
        ]);
        if (fsResp.status === "fulfilled") {
          const map = {};
          for (const s of (fsResp.value.data?.sources || [])) {
            map[s.field_name] = s;
          }
          setFieldSources(map);
        }
        setProcessingCases(
          pcResp.status === "fulfilled" && Array.isArray(pcResp.value.data) ? pcResp.value.data : []
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la încărcarea vânzării");
      navigate("/sales");
    } finally {
      setLoading(false);
    }
  };

  // P0.6 — open audit modal (history of business_status + sf_overwrote_manual entries)
  const openAuditDialog = async () => {
    setShowAuditDialog(true);
    try {
      const cId = sale?.contract_id || sale?.contract?.id;
      // Use backend `action_in` multi-value filter (P0.7) — avoids client-side filter
      const r = await axios.get(
        `${API_URL}/audit-logs?entity_type=contract&entity_id=${cId}&limit=30&action_in=business_status_change,sf_overwrote_manual,business_status_manual_set`
      );
      setAuditEntries(r.data?.data || []);
    } catch (e) {
      setAuditEntries([]);
    }
  };

  const fetchStatuses = async () => {
    try {
      const response = await axios.get(`${API_URL}/statuses`);
      setStatuses(response.data);
    } catch (error) {
      console.error("Failed to fetch statuses");
    }
  };

  useEffect(() => {
    fetchSale();
    fetchStatuses();
    // Iter31 P3b + Iter54 P1 perf — agents (dropdown ownership) DEFERRED + cache
    // sessionStorage 5 min: nu blochează randarea paginii (era ~1,5s per mount)
    (async () => {
      try {
        const cached = sessionStorage.getItem("agents-cache-v1");
        if (cached) {
          const { t, data } = JSON.parse(cached);
          if (Date.now() - t < 300000 && Array.isArray(data)) {
            setAgents(data);
            return;
          }
        }
      } catch (e) { /* ignore */ }
      try {
        const r = await axios.get(`${API_URL}/agents?limit=500`);
        const data = r.data?.data || r.data?.items || [];
        setAgents(data);
        try { sessionStorage.setItem("agents-cache-v1", JSON.stringify({ t: Date.now(), data })); } catch (e) { /* ignore */ }
      } catch (e) {
        // silent — panel falls back to ID-only display
      }
    })();
  }, [id]);

  const handleStatusChange = async () => {
    if (!newStatus || !sale?.contract_id) return;
    
    try {
      await axios.put(`${API_URL}/contracts/${sale.contract_id}/status`, { 
        internal_status: newStatus 
      });
      toast.success("Status actualizat");
      setStatusDialog(false);
      setNewStatus("");
      fetchSale();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la actualizarea statusului");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/sales/${id}`);
      toast.success("Vânzare ștearsă");
      navigate("/sales");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la ștergerea vânzării");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await axios.post(`${API_URL}/sales/${id}/documents?doc_type=scan`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Document încărcat");
      setUploadDialog(false);
      fetchSale();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la încărcarea documentului");
    } finally {
      setUploading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      "OK": "bg-emerald-50 text-emerald-700 border-emerald-200",
      "KO": "bg-red-50 text-red-700 border-red-200",
      "Draft": "bg-slate-100 text-slate-700 border-slate-200",
      "Trimis": "bg-blue-50 text-blue-700 border-blue-200",
      "În Procesare": "bg-amber-50 text-amber-700 border-amber-200",
      "Necesită Corecție": "bg-amber-50 text-amber-700 border-amber-200",
      "Anulat": "bg-gray-100 text-gray-700 border-gray-200"
    };
    return colors[status] || "bg-slate-100 text-slate-700 border-slate-200";
  };

  const getStatusIcon = (status) => {
    if (status === "OK") return <CheckCircle2 className="h-4 w-4" />;
    if (status === "KO") return <XCircle className="h-4 w-4" />;
    return <Clock className="h-4 w-4" />;
  };

  const getClientDisplay = () => {
    if (!sale?.client) return "-";
    const c = sale.client;
    if (c.client_type === "PF") {
      return c.full_name || `${c.last_name || ""} ${c.first_name || ""}`.trim() || c.cnp || "PF";
    }
    return c.company_name || c.full_name || c.last_name || c.cui || "PJ";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!sale) {
    return null;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Iter39 — FLAC scope indicator (visible only for restricted roles) */}
      <FlacScopeBanner flac={flac} entity="această vânzare" />
      {/* Header — afișat doar când NU suntem pe tab-ul Overview (Overview are propriul hero + breadcrumb după mockup) */}
      {activeTab !== "overview" && (
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4">
        <div className="flex items-start gap-2 md:gap-4">
          <Link to="/sales">
            <Button variant="ghost" size="icon" data-testid="back-btn" className="h-9 w-9 md:h-10 md:w-10">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg md:text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2 truncate">
              {sale.contract?.pod ? (
                <CopyableValue value={sale.contract.pod} mono label="POD" testId="header-pod-copyable" />
              ) : (
                sale.crm_internal_id || `Vânzare #${sale.id.slice(0, 8)}`
              )}
            </h1>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              {/* Iter53 P50 — Status general prominent (Faza B UI) */}
              <GeneralStatusBadge sale={sale} showConflicts={true} />
              <Badge
                className={`${getStatusColor(sale.status)} border text-[11px] md:text-xs cursor-help`}
                title="Status INTERN al fluxului BO (contract_status.internal_status) — pasul din procesarea internă. Nu confirmă activarea la furnizor (vezi Status SF / Status furnizor)."
                data-testid="header-internal-status"
              >
                {getStatusIcon(sale.status)}
                <span className="ml-1">Intern: {sale.status}</span>
              </Badge>
              {sale.contract?.business_status && (
                <>
                  <BusinessStatusBadge
                    status={sale.contract.business_status}
                    source={sale.contract.business_status_source}
                    date={sale.contract.business_status_date}
                    data-testid="business-status-badge"
                  />
                  <FieldSourceBadge
                    source={fieldSources.business_status?.source || sale.contract.business_status_source}
                    setAt={fieldSources.business_status?.set_at || sale.contract.business_status_date}
                    prevSource={fieldSources.business_status?.previous_source}
                    dataTestid="field-source-badge-business-status"
                  />
                </>
              )}
              {sale.contract?.commodity && (
                <Badge variant="outline" className="flex items-center gap-1 text-[11px] md:text-xs">
                  <Zap className="h-3 w-3" />
                  {sale.contract.commodity === "electric" ? "Energie" : "Gaz"}
                </Badge>
              )}
              {/* Iter52 P14x — Cluster status compus (SF Synced / QC / Match Agent) */}
              <SfStatusCluster sale={sale} />
            </div>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-1.5 md:gap-2">
          {hasRole(["admin", "backoffice"]) && (
            <Button variant="outline" size="sm" className="md:h-10" onClick={() => setStatusDialog(true)} data-testid="change-status-btn">
              <RefreshCw className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Schimbă Status</span>
              <span className="md:hidden ml-1">Status</span>
            </Button>
          )}
          {canEdit() && (
            <Link to={`/sales/${id}?edit=1`}>
              <Button variant="outline" size="sm" className="md:h-10" data-testid="edit-btn">
                <Edit className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Editează</span>
                <span className="md:hidden ml-1">Edit</span>
              </Button>
            </Link>
          )}
          {canDelete() && (
            <Button variant="destructive" size="sm" className="md:h-10" onClick={() => setDeleteDialog(true)} data-testid="delete-btn">
              <Trash2 className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Șterge</span>
            </Button>
          )}
          {/* Iter53 P22 — Trigger pentru sidebar Info rapide pe mobil */}
          <Button
            variant="outline"
            size="sm"
            className="md:hidden"
            onClick={() => setShowQuickInfoMobile(true)}
            data-testid="mobile-quickinfo-trigger"
          >
            <AlertCircle className="h-4 w-4 mr-1" />
            Info
          </Button>
        </div>
      </div>
      )}

      {/* P0.6 — Banner: SF a suprascris recent o modificare manuală (last 24h) */}
      {fieldSources.business_status?.source === "salesforce" &&
        fieldSources.business_status?.previous_source === "manual" &&
        minutesSince(fieldSources.business_status?.set_at) !== null &&
        minutesSince(fieldSources.business_status?.set_at) < 24 * 60 && (
          <div
            className="flex items-start gap-3 p-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-900"
            data-testid="sf-overwrote-manual-banner"
          >
            <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-medium">Status suprascris recent de Salesforce</p>
              <p className="text-amber-800 text-xs mt-0.5">
                O modificare manuală a fost înlocuită de un sync din SF în ultimele 24h.
                Verifică istoricul dacă a fost intenționat.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={openAuditDialog}
              className="border-amber-400 text-amber-900 hover:bg-amber-100"
              data-testid="btn-view-audit"
            >
              Vezi audit
            </Button>
          </div>
        )}

      {/* Iter53 P28/P32 — Rework banner (component extras în /SaleDetail/ReworkBanner.jsx) */}
      <ReworkBanner
        sale={sale}
        canResolve={hasRole(["admin", "backoffice"])}
        onResolveClick={() => setReworkResolveDialog(true)}
      />

      <div className={activeTab === "overview" ? "" : "grid grid-cols-1 lg:grid-cols-3 gap-6"}>
        {/* Main Content — Iter52 P14v: `min-w-0` previne ca un card cu conținut larg
            (e.g. badges/IDs lungi pe Tehnice/Audit) să forțeze col-span-2 peste sidebar. */}
        <div className={activeTab === "overview" ? "" : "lg:col-span-2 space-y-6 min-w-0"}>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            {/* Iter51 Etapa 7 — TabsList ascuns pe Overview (match 100% mockup, full-clean look) */}
            {/* Iter53 P22 — Pe mobil: buton care deschide BottomSheet cu lista de tab-uri.
                Pe desktop: TabsList orizontal clasic. */}
            {activeTab !== "overview" && (
              <>
                {/* Mobile tab selector trigger */}
                <button
                  type="button"
                  onClick={() => setShowTabSheet(true)}
                  className="md:hidden flex w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm"
                  data-testid="mobile-tab-trigger"
                  aria-haspopup="dialog"
                >
                  <span className="truncate">
                    Secțiune:&nbsp;
                    <span className="text-[#1e3a8a]">
                      {TAB_OPTIONS.find((o) => o.value === activeTab)?.label || activeTab}
                    </span>
                  </span>
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                </button>
                {/* Desktop horizontal tabs */}
                <TabsList className="hidden md:flex">
                  <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                  <TabsTrigger value="details" data-testid="tab-details">Detalii</TabsTrigger>
                  <TabsTrigger value="client" data-testid="tab-client">Client</TabsTrigger>
                  <TabsTrigger value="contract" data-testid="tab-contract">Contract</TabsTrigger>
                  <TabsTrigger value="salesforce" data-testid="tab-salesforce">Salesforce / Workflow</TabsTrigger>
                  <TabsTrigger value="documents" data-testid="tab-documents">Documente</TabsTrigger>
                  <TabsTrigger value="history" data-testid="tab-history">Istoric</TabsTrigger>
                  <TabsTrigger value="activity" data-testid="tab-activity">Activitate</TabsTrigger>
                  <TabsTrigger value="audit" data-testid="tab-audit">Tehnice & Audit</TabsTrigger>
                </TabsList>
              </>
            )}

            <TabsContent value="overview" className="mt-0">
              <SaleOverview sale={sale} onChange={fetchSale} flac={flac} canEdit={["admin", "backoffice", "manager"].includes(user?.role)} />
              {/* Iter53 P50 — General Status explainer (Faza B UI) */}
              <div className="mt-4">
                <GeneralStatusExplainer sale={sale} isAdmin={user?.role === "admin"} />
              </div>
              {/* Iter51 Etapa 7 — Quick-nav discret către celelalte tab-uri (înlocuiește TabsList pe Overview) */}
              <div className="mt-6 pt-4 border-t border-slate-200 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500" data-testid="overview-quick-nav">
                <span className="font-medium uppercase tracking-wider text-slate-400">Secțiuni avansate:</span>
                <button type="button" onClick={() => setActiveTab("details")} className="hover:text-blue-600 hover:underline" data-testid="quick-nav-details">Detalii</button>
                <span className="text-slate-300">·</span>
                <button type="button" onClick={() => setActiveTab("client")} className="hover:text-blue-600 hover:underline" data-testid="quick-nav-client">Client</button>
                <span className="text-slate-300">·</span>
                <button type="button" onClick={() => setActiveTab("contract")} className="hover:text-blue-600 hover:underline" data-testid="quick-nav-contract">Contract</button>
                <span className="text-slate-300">·</span>
                <button type="button" onClick={() => setActiveTab("salesforce")} className="hover:text-blue-600 hover:underline" data-testid="quick-nav-salesforce">SF / Workflow</button>
                <span className="text-slate-300">·</span>
                <button type="button" onClick={() => setActiveTab("documents")} className="hover:text-blue-600 hover:underline" data-testid="quick-nav-documents">Documente</button>
                <span className="text-slate-300">·</span>
                <button type="button" onClick={() => setActiveTab("history")} className="hover:text-blue-600 hover:underline" data-testid="quick-nav-history">Istoric</button>
                <span className="text-slate-300">·</span>
                <button type="button" onClick={() => setActiveTab("activity")} className="hover:text-blue-600 hover:underline" data-testid="quick-nav-activity">Activitate</button>
                <span className="text-slate-300">·</span>
                <button type="button" onClick={() => setActiveTab("audit")} className="hover:text-blue-600 hover:underline" data-testid="quick-nav-audit">Tehnice &amp; Audit</button>
              </div>
            </TabsContent>

            <TabsContent value="details" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Informații Vânzare</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-slate-500">Canal Vânzare</label>
                    <p className="mt-1 text-slate-900">{sale.channel || "-"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-500">Agenție</label>
                    <p className="mt-1 text-slate-900">{sale.agency?.name || "-"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-500">Nume Agent</label>
                    <p className="mt-1 text-slate-900">{sale.agent_name || "-"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-500">Cod Agent</label>
                    <p className="mt-1 text-slate-900">{sale.agent_code || "-"}</p>
                  </div>

                  {/* Iter31 P3b — Commercial Ownership panel */}
                  <CommercialOwnershipPanel
                    sale={sale}
                    agents={agents}
                    currentUserRole={user?.role}
                    onUpdate={fetchSale}
                  />

                  {/* Iter34a + Iter34b — Quality & data hygiene flags */}
                  {(sale.quality_call_outcome ||
                    sale.duplicate_pod_flag ||
                    sale.bad_data_flag ||
                    sale.customer_refusal_flag ||
                    sale.problem_reason_code ||
                    sale.requires_rework) && (
                    <div
                      className="col-span-2 rounded-lg border border-slate-200 bg-white p-3"
                      data-testid="quality-flags-panel"
                    >
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Quality &amp; Data flags
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {sale.quality_call_outcome && (
                          <Badge
                            className={
                              sale.quality_call_outcome === "ok"
                                ? "bg-emerald-100 text-emerald-900"
                                : "bg-rose-100 text-rose-900"
                            }
                            data-testid="quality-outcome-badge"
                          >
                            Quality: {sale.quality_call_outcome}
                          </Badge>
                        )}
                        {sale.duplicate_pod_flag && (
                          <Badge className="bg-amber-100 text-amber-900" data-testid="duplicate-pod-badge">
                            POD duplicat
                          </Badge>
                        )}
                        {sale.bad_data_flag && (
                          <Badge className="bg-rose-100 text-rose-900" data-testid="bad-data-badge">
                            Date greșite
                          </Badge>
                        )}
                        {sale.customer_refusal_flag && (
                          <Badge className="bg-rose-100 text-rose-900" data-testid="customer-refusal-badge">
                            Refuz client
                          </Badge>
                        )}
                        {sale.problem_reason_code && (
                          <Badge className="bg-orange-100 text-orange-900" data-testid="problem-reason-code-badge">
                            Cod: {sale.problem_reason_code}
                          </Badge>
                        )}
                        {sale.requires_rework && (
                          <Badge className="bg-amber-100 text-amber-900" data-testid="requires-rework-badge">
                            Necesită rework
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium text-slate-500">Operator Backoffice</label>
                    <p className="mt-1 text-slate-900">{sale.bo_operator_name || "-"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-500">Verificat BO</label>
                    <p className="mt-1 text-slate-900">{sale.bo_verified ? "Da" : "Nu"}</p>
                  </div>
                  {/* Iter42 — Upload tracking (status la furnizor) */}
                  <div data-testid="sale-uploaded">
                    <label className="text-sm font-medium text-slate-500">Încărcat la furnizor</label>
                    <div className="mt-1 flex items-center gap-2">
                      {sale.uploaded_flag ? (
                        <Badge className="bg-emerald-100 text-emerald-900" data-testid="uploaded-badge">
                          ✓ Încărcat
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-700" data-testid="not-uploaded-badge">
                          Neîncărcat
                        </Badge>
                      )}
                      {sale.uploaded_at && (
                        <span className="text-xs text-slate-500" data-testid="uploaded-at">
                          {sale.uploaded_at}
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-500">ID CRM Intern</label>
                    <p className="mt-1 text-slate-900">{sale.crm_internal_id || "-"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-500">Data Creare</label>
                    <p className="mt-1 text-slate-900">
                      {sale.created_at ? new Date(sale.created_at).toLocaleDateString("ro-RO") : "-"}
                    </p>
                  </div>
                  
                  <Separator className="col-span-2" />
                  
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-slate-500">Note Agent</label>
                    <p className="mt-1 text-slate-900">{sale.notes_agent || "-"}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-slate-500">Note Backoffice</label>
                    <p className="mt-1 text-slate-900">{sale.notes_bo || "-"}</p>
                  </div>
                  
                  {sale.problems && (
                    <div className="col-span-2 p-4 bg-red-50 rounded-lg border border-red-200" data-testid="sale-problems-block">
                      <div className="flex items-center gap-2 text-red-700 mb-2">
                        <AlertCircle className="h-4 w-4" />
                        <span className="font-medium">Probleme</span>
                        <Badge className="bg-red-100 text-red-800 border border-red-200 text-xs" data-testid="sale-problems-flag">
                          Da
                        </Badge>
                      </div>
                      <label className="text-xs font-medium text-red-700">Problema</label>
                      <p className="text-red-700 whitespace-pre-line text-sm mt-0.5" data-testid="sale-problems-detail">
                        {sale.problems_description || sale.notes_bo || "Marcat «cu probleme» în CRM Legacy (fără descriere textuală)."}
                      </p>
                      {sale.general_status === "Problemă" && (
                        <p className="mt-2 text-xs text-red-500" data-testid="sale-problems-status-note">
                          Statusul general de business este «Problemă» pe baza acestui flag.
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Iter36 — Returnare contract (condiționat) */}
              {(sale.returned || sale.return_date || sale.return_reason) && (
                <Card className="mt-6 border-amber-200" data-testid="return-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <span>↩️ Returnare contract</span>
                      {sale.returned && (
                        <Badge className="bg-amber-100 text-amber-900" data-testid="returned-badge">
                          Returnată
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription>
                      Vânzare returnată către agent pentru completare/recompletare.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    {sale.return_date && (
                      <div data-testid="return-date">
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Data returnare</label>
                        <p className="mt-1 text-slate-900">
                          {new Date(sale.return_date).toLocaleDateString("ro-RO")}
                        </p>
                      </div>
                    )}
                    {sale.return_reason && (
                      <div className="col-span-2" data-testid="return-reason">
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Motiv returnare</label>
                        <p className="mt-1 whitespace-pre-line text-slate-900">{sale.return_reason}</p>
                      </div>
                    )}
                    {sale.returned && (user?.role === "admin" || user?.role === "backoffice") && (
                      <div className="col-span-2 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            if (!window.confirm("Marchezi această vânzare ca recompletată și retrimisă?")) return;
                            try {
                              await axios.post(`${API_URL}/sales/${sale.id}/clear-return`, { note: "Recompletată din SaleDetail" });
                              toast.success("Returnarea a fost anulată. Vânzarea e re-activă.");
                              fetchSale();
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Eroare la anulare returnare");
                            }
                          }}
                          className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                          data-testid="clear-return-btn"
                        >
                          ✓ Marchează recompletată
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Iter36 — Mark as returned dialog launcher (visible when NOT returned, admin/BO only) */}
              {!sale.returned && (user?.role === "admin" || user?.role === "backoffice") && (
                <div className="mt-3 text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      const reason = window.prompt("Motiv returnare (obligatoriu, vizibil agentului):");
                      if (!reason || !reason.trim()) return;
                      try {
                        await axios.post(`${API_URL}/sales/${sale.id}/mark-returned`, { return_reason: reason.trim() });
                        toast.success("Vânzare marcată ca returnată.");
                        fetchSale();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Eroare la marcare returnată");
                      }
                    }}
                    className="text-amber-700 border-amber-300 hover:bg-amber-50"
                    data-testid="mark-returned-btn"
                  >
                    ↩️ Returnează către agent
                  </Button>
                </div>
              )}

              {/* Iter36 — Comisionare tracking (condiționat când există vreun semnal de comisionare) */}
              {(sale.commissioned !== null || sale.commission_month || sale.signing_month || sale.report_month) && (
                <Card className="mt-6" data-testid="commission-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <span>💰 Comisionare</span>
                      {sale.commissioned === true && (
                        <Badge className="bg-emerald-100 text-emerald-900" data-testid="commissioned-yes-badge">
                          Comisionată
                        </Badge>
                      )}
                      {sale.commissioned === false && (
                        <Badge className="bg-slate-100 text-slate-700" data-testid="commissioned-no-badge">
                          Necomisionată
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-4">
                    {sale.commission_month && (
                      <div data-testid="commission-month">
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Lună comision</label>
                        <p className="mt-1 text-slate-900">{sale.commission_month}</p>
                      </div>
                    )}
                    {sale.commissioned_at && (
                      <div data-testid="commissioned-at">
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Comisionată la</label>
                        <p className="mt-1 text-slate-900">
                          {new Date(sale.commissioned_at).toLocaleDateString("ro-RO")}
                        </p>
                      </div>
                    )}
                    {sale.signing_month && (
                      <div data-testid="signing-month">
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Lună semnare</label>
                        <p className="mt-1 text-slate-900">{sale.signing_month}</p>
                      </div>
                    )}
                    {sale.report_month && (
                      <div data-testid="report-month">
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Lună raportare</label>
                        <p className="mt-1 text-slate-900">{sale.report_month}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="client" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Informații Client</CardTitle>
                  <CardDescription>
                    {sale.client?.client_type === "PF" ? "Persoană Fizică" : "Persoană Juridică"}
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-6">
                  {sale.client?.client_type === "PF" ? (
                    <>
                      <div>
                        <label className="text-sm font-medium text-slate-500">CNP</label>
                        <p className="mt-1 text-slate-900">{sale.client?.cnp || "-"}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-500">Nume Complet</label>
                        <p className="mt-1 text-slate-900">
                          {sale.client?.full_name || `${sale.client?.last_name || ""} ${sale.client?.first_name || ""}`.trim() || "-"}
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="text-sm font-medium text-slate-500">CUI</label>
                        <p className="mt-1 text-slate-900">{sale.client?.cui || "-"}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-500">Denumire Firmă</label>
                        <p className="mt-1 text-slate-900">{sale.client?.company_name || "-"}</p>
                      </div>
                    </>
                  )}
                  <div>
                    <label className="text-sm font-medium text-slate-500">Județ</label>
                    <p className="mt-1 text-slate-900">{sale.client?.county || "-"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-500">Localitate</label>
                    <p className="mt-1 text-slate-900">{sale.client?.locality || "-"}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-slate-500">Stradă</label>
                    <p className="mt-1 text-slate-900">{sale.client?.street || "-"}</p>
                  </div>
                  {sale.client?.phone_mobile && (
                    <div>
                      <label className="text-sm font-medium text-slate-500">Telefon Mobil</label>
                      <p className="mt-1 text-slate-900">{sale.client.phone_mobile}</p>
                    </div>
                  )}
                  {/* Iter42 — Telefon fix (lângă mobil) */}
                  {sale.client?.phone_fixed && (
                    <div data-testid="client-phone-fixed">
                      <label className="text-sm font-medium text-slate-500">Telefon Fix</label>
                      <p className="mt-1 text-slate-900">{sale.client.phone_fixed}</p>
                    </div>
                  )}
                  {sale.client?.email && (
                    <div>
                      <label className="text-sm font-medium text-slate-500">Email</label>
                      <p className="mt-1 text-slate-900">{sale.client.email}</p>
                    </div>
                  )}
                  {/* Iter42 — Identity & address extras */}
                  {sale.client?.id_series && (
                    <div data-testid="client-id-series">
                      <label className="text-sm font-medium text-slate-500">Serie buletin</label>
                      <p className="mt-1 font-mono text-slate-900">{sale.client.id_series}</p>
                    </div>
                  )}
                  {sale.client?.postal_code && (
                    <div data-testid="client-postal-code">
                      <label className="text-sm font-medium text-slate-500">Cod poștal</label>
                      <p className="mt-1 text-slate-900">{sale.client.postal_code}</p>
                    </div>
                  )}
                  {sale.client?.address_raw && (
                    <div className="col-span-2" data-testid="client-address-raw">
                      <label className="text-sm font-medium text-slate-500">Adresă completă (raw)</label>
                      <p className="mt-1 whitespace-pre-line text-slate-900">{sale.client.address_raw}</p>
                    </div>
                  )}
                  {/* Iter42 — Legacy representative (only if any populated — preserves history) */}
                  {(sale.client?.representative_name || sale.client?.representative_cnp || sale.client?.representative_phone || sale.client?.representative_email) && (
                    <div className="col-span-2 rounded-md border border-amber-200 bg-amber-50/40 p-3" data-testid="legacy-rep-block">
                      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Reprezentant (legacy — date moștenite)
                        <Badge variant="outline" className="text-[9px]">legacy</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        {sale.client?.representative_name && (
                          <div><label className="text-xs text-slate-500">Nume</label><p className="text-slate-900">{sale.client.representative_name}</p></div>
                        )}
                        {sale.client?.representative_cnp && (
                          <div><label className="text-xs text-slate-500">CNP</label><p className="font-mono text-slate-900">{sale.client.representative_cnp}</p></div>
                        )}
                        {sale.client?.representative_phone && (
                          <div><label className="text-xs text-slate-500">Telefon</label><p className="text-slate-900">{sale.client.representative_phone}</p></div>
                        )}
                        {sale.client?.representative_email && (
                          <div><label className="text-xs text-slate-500">Email</label><p className="text-slate-900">{sale.client.representative_email}</p></div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Iter35 + Iter41 — Legal representative (PJ, canonical) */}
                  {(sale.client?.legal_representative_name || sale.client?.legal_representative_cnp || sale.client?.legal_representative_id_series || sale.client?.legal_representative_role) && (
                    <div className="col-span-2 rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="legal-representative-block">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Reprezentant legal (semnatar contract)
                      </div>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        {sale.client.legal_representative_name && (
                          <div data-testid="legal-rep-name">
                            <label className="text-xs text-slate-500">Nume</label>
                            <p className="text-slate-900">{sale.client.legal_representative_name}</p>
                          </div>
                        )}
                        {sale.client.legal_representative_cnp && (
                          <div data-testid="legal-rep-cnp">
                            <label className="text-xs text-slate-500">CNP semnatar</label>
                            <p className="font-mono text-slate-900">{sale.client.legal_representative_cnp}</p>
                          </div>
                        )}
                        {/* Iter42 — extended legal rep fields surfaced in SaleDetail */}
                        {sale.client.legal_representative_id_series && (
                          <div data-testid="legal-rep-id-series">
                            <label className="text-xs text-slate-500">Serie buletin semnatar</label>
                            <p className="font-mono text-slate-900">{sale.client.legal_representative_id_series}</p>
                          </div>
                        )}
                        {sale.client.legal_representative_role && (
                          <div data-testid="legal-rep-role">
                            <label className="text-xs text-slate-500">Funcție în firmă</label>
                            <p className="text-slate-900">{sale.client.legal_representative_role}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Iter52 P14l — GDPR badges removed from sale page (per user
                      decision 2026-02-18). Consent info still displayed on the
                      Client details page (read-only), data preserved in DB. */}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="contract" className="mt-6">
              <Card data-testid="card-detalii-contract">
                <CardHeader>
                  <CardTitle>Informații Contract</CardTitle>
                  <CardDescription>
                    Date tehnice despre punctul de consum
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-6">
                  <div className="min-w-0">
                    <label className="text-sm font-medium text-slate-500">POD</label>
                    <p className="mt-1 text-slate-900 font-mono break-all">{sale.contract?.pod || "-"}</p>
                  </div>
                  <div className="min-w-0">
                    <label className="text-sm font-medium text-slate-500">NLC</label>
                    <p className="mt-1 text-slate-900 font-mono break-all">{sale.contract?.nlc || "-"}</p>
                  </div>
                  {sale.contract?.nlc_gaz && (
                    <div data-testid="contract-nlc-gaz" className="min-w-0">
                      <label className="text-sm font-medium text-slate-500">NLC Gaz</label>
                      <p className="mt-1 text-slate-900 font-mono break-all">{sale.contract.nlc_gaz}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-slate-500">Tip Energie</label>
                    <p className="mt-1 text-slate-900">
                      {sale.contract?.commodity === "electric" ? "Energie Electrică" : 
                       sale.contract?.commodity === "gas" ? "Gaz Natural" : "-"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-500">Produs</label>
                    <p className="mt-1 flex items-center gap-2 text-slate-900">
                      {sale.contract?.product_name || "-"}
                      {/* Iter42 — Bundle flag (VAS included) */}
                      {sale.contract?.is_bundle && (
                        <Badge className="bg-violet-100 text-violet-900 text-[10px]" data-testid="contract-is-bundle-badge">
                          Bundle (VAS inclus)
                        </Badge>
                      )}
                    </p>
                  </div>
                  {sale.contract?.raw_product_name &&
                    sale.contract.raw_product_name !== sale.contract.product_name && (
                      <div data-testid="contract-raw-product">
                        <label className="text-sm font-medium text-slate-500">Produs (brut din import)</label>
                        <p className="mt-1 text-slate-900 italic">{sale.contract.raw_product_name}</p>
                      </div>
                    )}
                  {sale.contract?.offer_type && (
                    <div data-testid="contract-offer-type">
                      <label className="text-sm font-medium text-slate-500">Tip ofertă (piață liberă)</label>
                      <p className="mt-1 text-slate-900">{sale.contract.offer_type}</p>
                    </div>
                  )}
                  {sale.contract?.price_kwh && (
                    <div data-testid="contract-price-kwh">
                      <label className="text-sm font-medium text-slate-500">Preț kWh electric</label>
                      <p className="mt-1 text-slate-900 font-mono">{sale.contract.price_kwh}</p>
                    </div>
                  )}
                  {sale.contract?.price_kwh_gas && (
                    <div data-testid="contract-price-kwh-gas">
                      <label className="text-sm font-medium text-slate-500">Preț kWh gaz</label>
                      <p className="mt-1 text-slate-900 font-mono">{sale.contract.price_kwh_gas}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-slate-500">Zonă Distribuție</label>
                    <p className="mt-1 text-slate-900">{sale.contract?.distribution_zone || "-"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-500">Furnizor Anterior</label>
                    <p className="mt-1 text-slate-900">{sale.contract?.previous_supplier || "-"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-500">Data Semnării</label>
                    <p className="mt-1 text-slate-900" data-testid="contract-signed-date">{formatRoDate(sale.contract?.signed_date) || "-"}</p>
                  </div>
                  {sale.contract?.contract_date &&
                    sale.contract.contract_date !== sale.contract.signed_date && (
                      <div data-testid="contract-date-admin">
                        <label className="text-sm font-medium text-slate-500">Data contract (admin)</label>
                        <p className="mt-1 text-slate-900">{sale.contract.contract_date}</p>
                      </div>
                    )}
                  {sale.contract?.start_date && (
                    <div data-testid="contract-start-date">
                      <label className="text-sm font-medium text-slate-500">Data început</label>
                      <p className="mt-1 text-slate-900">{sale.contract.start_date}</p>
                    </div>
                  )}
                  {sale.contract?.contract_duration_months && (
                    <div data-testid="contract-duration">
                      <label className="text-sm font-medium text-slate-500">Durată contract</label>
                      <p className="mt-1 text-slate-900">{sale.contract.contract_duration_months} luni</p>
                    </div>
                  )}
                  {/* Iter42 — Nr. contract intern CRM (P0 operational) */}
                  {sale.contract?.contract_number && (
                    <div data-testid="contract-number">
                      <label className="text-sm font-medium text-slate-500">
                        Nr. contract <span className="text-xs text-slate-400 font-normal">(real, semnat)</span>
                      </label>
                      <p className="mt-1 font-mono text-slate-900">{sale.contract.contract_number}</p>
                    </div>
                  )}
                  {/* Iter52 — Per-source contract identifiers */}
                  {sale.contract?.sf_contract_id && (
                    <div data-testid="sf-contract-id">
                      <label className="text-sm font-medium text-slate-500 flex items-center gap-1.5">
                        ID contract SF
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200">Salesforce</span>
                      </label>
                      <p className="mt-1 font-mono text-slate-900">{sale.contract.sf_contract_id}</p>
                    </div>
                  )}
                  {sale.contract?.easy_contract_id && (
                    <div data-testid="easy-contract-id">
                      <label className="text-sm font-medium text-slate-500 flex items-center gap-1.5">
                        ID contract Easy
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Easy App</span>
                      </label>
                      <p className="mt-1 font-mono text-slate-900">{sale.contract.easy_contract_id}</p>
                    </div>
                  )}
                  {sale.contract?.contract_number_old && (
                    <div data-testid="contract-number-old">
                      <label className="text-sm font-medium text-slate-500">Nr. contract vechi</label>
                      <p className="mt-1 text-slate-900">{sale.contract.contract_number_old}</p>
                    </div>
                  )}
                  {sale.contract?.clc && (
                    <div data-testid="contract-clc">
                      <label className="text-sm font-medium text-slate-500">CLC</label>
                      <p className="mt-1 font-mono text-slate-900">{sale.contract.clc}</p>
                    </div>
                  )}
                  {sale.contract?.pdr && (
                    <div data-testid="contract-pdr">
                      <label className="text-sm font-medium text-slate-500">PDR (gaz)</label>
                      <p className="mt-1 font-mono text-slate-900">{sale.contract.pdr}</p>
                    </div>
                  )}
                  {sale.contract?.numar_loc_consum && (
                    <div data-testid="contract-numar-loc">
                      <label className="text-sm font-medium text-slate-500">Nr. loc consum</label>
                      <p className="mt-1 font-mono text-slate-900">{sale.contract.numar_loc_consum}</p>
                    </div>
                  )}
                  {sale.contract?.tip_loc_consum && (
                    <div data-testid="contract-tip-loc">
                      <label className="text-sm font-medium text-slate-500">Tip loc consum</label>
                      <p className="mt-1 text-slate-900">{sale.contract.tip_loc_consum}</p>
                    </div>
                  )}
                  {sale.contract?.customer_category && (
                    <div data-testid="contract-customer-category">
                      <label className="text-sm font-medium text-slate-500">Categorie client</label>
                      <p className="mt-1 text-slate-900">{sale.contract.customer_category}</p>
                    </div>
                  )}
                  {sale.contract?.contract_type && (
                    <div data-testid="contract-type">
                      <label className="text-sm font-medium text-slate-500">Tip contract</label>
                      <p className="mt-1 text-slate-900">{sale.contract.contract_type}</p>
                    </div>
                  )}
                  {sale.contract?.commercial_product_name && (
                    <div data-testid="contract-commercial-product">
                      <label className="text-sm font-medium text-slate-500">Nume comercial produs</label>
                      <p className="mt-1 text-slate-900">{sale.contract.commercial_product_name}</p>
                    </div>
                  )}
                  {sale.contract?.distributor_name && (
                    <div data-testid="contract-distributor">
                      <label className="text-sm font-medium text-slate-500">Distribuitor</label>
                      <p className="mt-1 text-slate-900">{sale.contract.distributor_name}</p>
                    </div>
                  )}
                  {sale.contract?.estimated_annual_kwh && (
                    <div data-testid="contract-kwh-ee">
                      <label className="text-sm font-medium text-slate-500">Consum estimat EE (kWh)</label>
                      <p className="mt-1 font-mono text-slate-900">{sale.contract.estimated_annual_kwh}</p>
                    </div>
                  )}
                  {sale.contract?.estimated_annual_kwh_gas && (
                    <div data-testid="contract-kwh-gas">
                      <label className="text-sm font-medium text-slate-500">Consum estimat gaz</label>
                      <p className="mt-1 font-mono text-slate-900">{sale.contract.estimated_annual_kwh_gas}</p>
                    </div>
                  )}
                  {sale.contract?.activation_date && (
                    <div data-testid="contract-activation-date">
                      <label className="text-sm font-medium text-slate-500">Data activare</label>
                      <p className="mt-1 text-slate-900">{sale.contract.activation_date}</p>
                    </div>
                  )}
                  {sale.contract?.cancellation_date && (
                    <div data-testid="contract-cancellation-date">
                      <label className="text-sm font-medium text-slate-500 text-rose-600">Data anulare</label>
                      <p className="mt-1 text-rose-900">{sale.contract.cancellation_date}</p>
                    </div>
                  )}
                  {(sale.contract?.consumption_county || sale.contract?.consumption_locality || sale.contract?.consumption_street) && (
                    <div className="col-span-2" data-testid="contract-consumption-address">
                      <label className="text-sm font-medium text-slate-500">Adresă loc consum</label>
                      <p className="mt-1 text-slate-900">
                        {[sale.contract.consumption_street, sale.contract.consumption_locality, sale.contract.consumption_county]
                          .filter(Boolean).join(", ") || "—"}
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-slate-500">Consum Anual Estimat</label>
                    <p className="mt-1 text-slate-900">
                      {sale.contract?.estimated_annual_kwh 
                        ? `${sale.contract.estimated_annual_kwh.toLocaleString()} kWh`
                        : "-"
                      }
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-500 flex items-center gap-2">
                      Data CSD (începere furnizare)
                      <FieldSourceBadge
                        source={fieldSources.csd_date?.source}
                        setAt={fieldSources.csd_date?.set_at}
                        prevSource={fieldSources.csd_date?.previous_source}
                        dataTestid="fs-badge-csd-date"
                        fieldLabel="CSD"
                      />
                    </label>
                    <p className="mt-1 text-slate-900" data-testid="csd-date-value">{sale.contract?.csd_date || "-"}</p>
                  </div>

                  {/* Iter53 P27 — Status furnizor + Data status furnizor mutate aici (din card separat).
                      Editabile doar pentru admin/backoffice. */}
                  <div className="col-span-2 mt-2 pt-4 border-t border-slate-200" data-testid="supplier-status-section">
                    <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
                      <Zap className="h-4 w-4 text-indigo-600" />
                      Status furnizor
                    </h4>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm font-medium text-slate-500 flex items-center gap-2">
                          Status furnizor
                          <FieldSourceBadge
                            source={fieldSources.supplier_status?.source}
                            setAt={fieldSources.supplier_status?.set_at}
                            prevSource={fieldSources.supplier_status?.previous_source}
                            dataTestid="fs-badge-supplier-status"
                        fieldLabel="status furnizor"
                          />
                        </label>
                        {hasRole(["admin", "backoffice"]) ? (
                          <select
                            className="mt-1 w-full border rounded-md p-2 text-sm bg-white"
                            value={sale.contract?.supplier_status || ""}
                            data-testid="supplier-status-inline-select"
                            onChange={async (e) => {
                              const next = e.target.value || null;
                              try {
                                await axios.put(
                                  `${API_URL}/contracts/${sale.contract.id}/supplier-fields`,
                                  {
                                    supplier_status: next,
                                    fields_to_patch: ["supplier_status"],
                                    reason: "inline edit din pagina vânzării",
                                  },
                                );
                                toast.success(next ? `Status setat: ${next}` : "Status șters");
                                await fetchSale();
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "Eroare la salvare");
                              }
                            }}
                          >
                            <option value="">— fără status —</option>
                            <option value="Signed">Signed (semnat)</option>
                            <option value="Activated">Activated (activ)</option>
                            <option value="Terminated">Terminated (reziliat)</option>
                          </select>
                        ) : (
                          <p className="mt-1 text-slate-900" data-testid="supplier-status-value">
                            {sale.contract?.supplier_status || "-"}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-500 flex items-center gap-2">
                          Data status furnizor
                          <FieldSourceBadge
                            source={fieldSources.supplier_status_date?.source}
                            setAt={fieldSources.supplier_status_date?.set_at}
                            prevSource={fieldSources.supplier_status_date?.previous_source}
                            dataTestid="fs-badge-supplier-status-date"
                        fieldLabel="dată status furnizor"
                          />
                        </label>
                        <p className="mt-1 text-slate-900" data-testid="supplier-status-date-value">
                          {sale.contract?.supplier_status_date
                            ? new Date(sale.contract.supplier_status_date).toLocaleString("ro-RO")
                            : "-"}
                        </p>
                      </div>
                    </div>
                    {hasRole(["admin", "backoffice"]) && (
                      <div className="mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSupplierEditDialog(true)}
                          data-testid="edit-supplier-fields-btn"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Editare avansată (cu Data status + CSD)
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Iter53 P27 — Note backoffice mutate aici, la final. Editabile doar admin/backoffice. */}
                  <div className="col-span-2 pt-4 border-t border-slate-200" data-testid="notes-bo-section">
                    <label className="text-sm font-medium text-slate-500 flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-slate-500" />
                        Note backoffice
                      </span>
                      {sale.notes_bo && (
                        <span className="text-[10px] text-slate-400">
                          {sale.notes_bo.length} caractere
                        </span>
                      )}
                    </label>
                    {hasRole(["admin", "backoffice"]) ? (
                      <NotesInlineEditor
                        initialValue={sale.notes_bo || ""}
                        saleId={sale.id}
                        onSaved={fetchSale}
                      />
                    ) : (
                      <p className="mt-1 text-sm text-slate-900 whitespace-pre-wrap" data-testid="notes-bo-value">
                        {sale.notes_bo || <span className="text-slate-400 italic">— fără note —</span>}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="salesforce" className="mt-6 space-y-6">
              {/* Iter35 — Salesforce / Workflow tab: ProcessingCase details + SF actor tracking + cancellation/QC fields */}
              <Card data-testid="sf-workflow-card">
                <CardHeader>
                  <CardTitle className="text-base">Workflow Salesforce — cazuri & dosar</CardTitle>
                  <CardDescription>
                    Detalii granulare per dosar SF (fază, tip proces, validare documente). Datele sunt sincronizate la fiecare import SF.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {processingCases.length === 0 && (
                    <p className="text-sm italic text-slate-400" data-testid="sf-no-cases">
                      Niciun caz Salesforce sincronizat pentru acest contract.
                    </p>
                  )}
                  {processingCases.length > 0 && (
                    <div className="space-y-3">
                      {processingCases.map((pc) => (
                        <div
                          key={pc.id}
                          className="rounded-md border border-slate-200 p-3 text-sm"
                          data-testid={`sf-case-${pc.id}`}
                        >
                          <div className="mb-2 flex items-center justify-between">
                            <div>
                              <span className="font-mono text-xs text-slate-500">Dosar:</span>{" "}
                              <span className="font-medium">{pc.dossier_number || "—"}</span>
                              {pc.case_number && (
                                <>
                                  <span className="ml-2 font-mono text-xs text-slate-500">Caz:</span>{" "}
                                  <span className="font-medium">{pc.case_number}</span>
                                </>
                              )}
                            </div>
                            <Badge variant="outline" className="text-[10px]">{pc.source_system || "—"}</Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs md:grid-cols-3">
                            {pc.case_status && (
                              <div
                                data-testid="sf-case-status"
                                title={pc.source_system === "easy"
                                  ? "Status raportat de partener prin Easy App pentru acest dosar („Activated” în Easy = dosar activat în aplicația partenerului, nu confirmarea furnizorului)."
                                  : `Status caz raportat de ${pc.source_system || "sursă"} pentru acest dosar.`}
                              >
                                <span className="text-slate-500">Case Status ({pc.source_system || "?"}):</span>{" "}
                                <span className="font-medium">{pc.case_status}</span>
                              </div>
                            )}
                            {pc.dossier_phase && (
                              <div data-testid="sf-dossier-phase">
                                <span className="text-slate-500">Fază dosar:</span>{" "}
                                <span className="font-medium">{pc.dossier_phase}</span>
                              </div>
                            )}
                            {pc.case_process_type && (
                              <div data-testid="sf-case-process-type">
                                <span className="text-slate-500">Tip proces:</span>{" "}
                                <span className="font-medium">{pc.case_process_type}</span>
                              </div>
                            )}
                            {pc.case_record_type && (
                              <div>
                                <span className="text-slate-500">Tip înregistrare:</span>{" "}
                                <span className="font-medium">{pc.case_record_type}</span>
                              </div>
                            )}
                            {pc.phase_description && (
                              <div className="col-span-2">
                                <span className="text-slate-500">Descriere fază:</span>{" "}
                                <span>{pc.phase_description}</span>
                              </div>
                            )}
                            {pc.source_status && (
                              <div>
                                <span className="text-slate-500">Status sursă (raw):</span>{" "}
                                <span>{pc.source_status}</span>
                              </div>
                            )}
                            {pc.opportunity_created_at && (
                              <div>
                                <span className="text-slate-500">Oport. creată:</span>{" "}
                                <span>{new Date(pc.opportunity_created_at).toLocaleDateString("ro-RO")}</span>
                              </div>
                            )}
                            {pc.document_validation_date && (
                              <div>
                                <span className="text-slate-500">Doc validate:</span>{" "}
                                <span>{new Date(pc.document_validation_date).toLocaleDateString("ro-RO")}</span>
                              </div>
                            )}
                            {pc.request_confirmed_at && (
                              <div>
                                <span className="text-slate-500">Req. confirmat:</span>{" "}
                                <span>{new Date(pc.request_confirmed_at).toLocaleDateString("ro-RO")}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Iter37 — Status SF (read-only, populated via SF webhook/import) */}
              {(sale.contract_status?.salesforce_contract_state || sale.contract_status?.salesforce_status_date) && (
                <Card data-testid="sf-status-card">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <span>Status SF</span>
                      <Badge variant="outline" className="text-[10px]">read-only</Badge>
                    </CardTitle>
                    <CardDescription>
                      Stare contract sincronizată din Salesforce (populată automat prin webhook/import).
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-2">
                    {sale.contract_status.salesforce_contract_state && (
                      <div data-testid="sf-contract-state">
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          Stare contract (SF)
                        </label>
                        <div className="mt-1">
                          {(() => {
                            const st = sale.contract_status.salesforce_contract_state;
                            const stl = String(st).toLowerCase();
                            let cls = "bg-slate-100 text-slate-800";
                            if (stl.includes("signed") || stl.includes("activated") || stl.includes("active") || stl.includes("approved")) {
                              cls = "bg-emerald-100 text-emerald-900";
                            } else if (stl.includes("cancel") || stl.includes("rejected") || stl.includes("anul")) {
                              cls = "bg-rose-100 text-rose-900";
                            } else if (stl.includes("pending") || stl.includes("draft") || stl.includes("review") || stl.includes("quality")) {
                              cls = "bg-amber-100 text-amber-900";
                            }
                            return <Badge className={cls}>{st}</Badge>;
                          })()}
                        </div>
                      </div>
                    )}
                    {sale.contract_status.salesforce_status_date && (
                      <div data-testid="sf-status-date">
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          Data status SF
                        </label>
                        <p className="mt-1 text-slate-900">
                          {(() => {
                            const d = new Date(sale.contract_status.salesforce_status_date);
                            const dd = String(d.getDate()).padStart(2, "0");
                            const mm = String(d.getMonth() + 1).padStart(2, "0");
                            const yyyy = d.getFullYear();
                            const hh = String(d.getHours()).padStart(2, "0");
                            const mi = String(d.getMinutes()).padStart(2, "0");
                            // Plain Date type — display dd.mm.yyyy (no time component in DB)
                            return `${dd}.${mm}.${yyyy}${hh === "00" && mi === "00" ? "" : ` ${hh}:${mi}`}`;
                          })()}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Iter37 — Anulare (vizibil DOAR dacă state ∈ CANCELLED/REJECTED) */}
              {(() => {
                const st = String(sale.contract_status?.salesforce_contract_state || "").toLowerCase();
                const isCancelOrReject = st.includes("cancel") || st.includes("rejected") || st.includes("anul");
                const hasData = sale.contract_status?.cancellation_reason || sale.contract_status?.cancellation_details;
                if (!isCancelOrReject && !hasData) return null;
                return (
                  <Card className="border-rose-200" data-testid="sf-cancellation-card">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <span className="text-rose-700">⛔ Anulare</span>
                        <Badge variant="outline" className="text-[10px]">read-only</Badge>
                      </CardTitle>
                      <CardDescription>
                        Detalii anulare/respingere sincronizate din Salesforce.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 gap-3 text-sm">
                      {sale.contract_status?.cancellation_reason ? (
                        <div data-testid="sf-cancellation-reason">
                          <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
                            Motiv anulare (SF)
                          </label>
                          <p className="mt-1 text-slate-900">{sale.contract_status.cancellation_reason}</p>
                        </div>
                      ) : (
                        <div className="text-xs italic text-slate-400" data-testid="sf-cancellation-reason-empty">
                          Niciun motiv anulare sincronizat încă.
                        </div>
                      )}
                      {sale.contract_status?.cancellation_details && (
                        <div data-testid="sf-cancellation-details">
                          <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
                            Detalii anulare (SF)
                          </label>
                          <p className="mt-1 whitespace-pre-line text-slate-900">{sale.contract_status.cancellation_details}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Iter37 — Quality Call (status indicator dacă e completat sau nu) */}
              <Card data-testid="sf-quality-call-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <span>Quality Call</span>
                    {sale.contract_status?.quality_call_date ? (
                      <Badge className="bg-emerald-100 text-emerald-900" data-testid="qc-status-completed">
                        Completat
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-900" data-testid="qc-status-pending">
                        Necompletat
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-[10px]">read-only</Badge>
                  </CardTitle>
                  <CardDescription>
                    Dată apel Quality Call la nivel de contract (sincronizat din SF).
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-2">
                  {sale.contract_status?.quality_call_date ? (
                    <div data-testid="sf-quality-call-date">
                      <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Dată Quality Call
                      </label>
                      <p className="mt-1 text-slate-900">
                        {(() => {
                          const d = new Date(sale.contract_status.quality_call_date);
                          const dd = String(d.getDate()).padStart(2, "0");
                          const mm = String(d.getMonth() + 1).padStart(2, "0");
                          const yyyy = d.getFullYear();
                          return `${dd}.${mm}.${yyyy}`;
                        })()}
                      </p>
                    </div>
                  ) : (
                    <div className="col-span-2 text-xs italic text-slate-400" data-testid="sf-quality-call-empty">
                      Niciun Quality Call sincronizat pentru acest contract.
                    </div>
                  )}
                  {sale.contract_status?.quality_call_outcome && (
                    <div data-testid="sf-quality-call-outcome">
                      <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Rezultat QC
                      </label>
                      <p className="mt-1 text-slate-900">{sale.contract_status.quality_call_outcome}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* QC + Cancellation details */}
              {(sale.cancellation_reason_text || sale.rejection_subreason ||
                sale.qc_call_date || sale.qc_operator_name) && (
                <Card data-testid="qc-cancellation-card">
                  <CardHeader>
                    <CardTitle className="text-base">QC & Anulare</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-2">
                    {sale.qc_call_date && (
                      <div data-testid="qc-call-date">
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Dată apel QC</label>
                        <p className="mt-1 text-slate-900">
                          {new Date(sale.qc_call_date).toLocaleString("ro-RO")}
                        </p>
                      </div>
                    )}
                    {sale.qc_operator_name && (
                      <div data-testid="qc-operator-name">
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Operator QC</label>
                        <p className="mt-1 text-slate-900">{sale.qc_operator_name}</p>
                      </div>
                    )}
                    {sale.cancellation_reason_text && (
                      <div className="col-span-2" data-testid="cancellation-reason-text">
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Motiv anulare (SF)</label>
                        <p className="mt-1 whitespace-pre-line text-slate-900">{sale.cancellation_reason_text}</p>
                      </div>
                    )}
                    {sale.rejection_subreason && (
                      <div className="col-span-2" data-testid="rejection-subreason">
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Sub-motiv respingere</label>
                        <p className="mt-1 text-slate-900">{sale.rejection_subreason}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Supplier-side fields */}
              {(sale.contract?.business_status || sale.contract?.supplier_status ||
                sale.contract?.supplier_ok || sale.contract?.supplier_ko) && (
                <Card data-testid="supplier-status-card">
                  <CardHeader>
                    <CardTitle className="text-base">Status furnizor (SF)</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-3">
                    {sale.contract.supplier_status && (
                      <div>
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Status furnizor</label>
                        <p className="mt-1 text-slate-900">{sale.contract.supplier_status}</p>
                      </div>
                    )}
                    {sale.contract.supplier_ok && (
                      <div>
                        <Badge className="bg-emerald-100 text-emerald-900">Supplier OK</Badge>
                      </div>
                    )}
                    {sale.contract.supplier_ko && (
                      <div>
                        <Badge className="bg-rose-100 text-rose-900">Supplier KO</Badge>
                      </div>
                    )}
                    {sale.contract.supplier_status_date && (
                      <div>
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Data status</label>
                        <p className="mt-1 text-slate-900">
                          {new Date(sale.contract.supplier_status_date).toLocaleDateString("ro-RO")}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Audit lifecycle */}
              <Card data-testid="audit-lifecycle-card">
                <CardHeader>
                  <CardTitle className="text-base">Trasabilitate import & lifecycle</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-3">
                  {sale.first_imported_at && (
                    <div data-testid="first-imported-at">
                      <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Primul import</label>
                      <p className="mt-1 text-slate-900">
                        {new Date(sale.first_imported_at).toLocaleString("ro-RO")}
                      </p>
                    </div>
                  )}
                  {sale.activation_pending_since && (
                    <div data-testid="activation-pending-since">
                      <label className="text-xs font-medium uppercase tracking-wide text-slate-500">În Activare din</label>
                      <p className="mt-1 text-slate-900">
                        {new Date(sale.activation_pending_since).toLocaleString("ro-RO")}
                      </p>
                    </div>
                  )}
                  {sale.contract?.last_synced_from_sf_at && (
                    <div data-testid="last-synced-from-sf">
                      <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Ultim sync SF</label>
                      <p className="mt-1 text-slate-900">
                        {new Date(sale.contract.last_synced_from_sf_at).toLocaleString("ro-RO")}
                      </p>
                    </div>
                  )}
                  {sale.external_legacy_id && (
                    <div data-testid="external-legacy-id">
                      <label className="text-xs font-medium uppercase tracking-wide text-slate-500">ID Legacy CRM</label>
                      <p className="mt-1 font-mono text-xs text-slate-700">{sale.external_legacy_id}</p>
                    </div>
                  )}
                  {sale.contract?.external_salesforce_id && (
                    <div data-testid="external-salesforce-id">
                      <label className="text-xs font-medium uppercase tracking-wide text-slate-500">ID Salesforce</label>
                      <p className="mt-1 font-mono text-xs text-slate-700">{sale.contract.external_salesforce_id}</p>
                    </div>
                  )}
                  {sale.sales_origin && (
                    <div data-testid="sales-origin">
                      <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Origine SF</label>
                      <p className="mt-1 text-slate-900">{sale.sales_origin}</p>
                    </div>
                  )}
                  {sale.sales_unit_external_name && (
                    <div data-testid="sales-unit">
                      <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Sales Unit SF</label>
                      <p className="mt-1 text-slate-900">{sale.sales_unit_external_name}</p>
                    </div>
                  )}
                  {sale.bo_received_at && (
                    <div>
                      <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Primire BO</label>
                      <p className="mt-1 text-slate-900">{sale.bo_received_at}</p>
                    </div>
                  )}
                  {sale.bo_uploaded_at && (
                    <div>
                      <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Upload BO</label>
                      <p className="mt-1 text-slate-900">
                        {new Date(sale.bo_uploaded_at).toLocaleString("ro-RO")}
                      </p>
                    </div>
                  )}
                  {(sale.pin_sms_sent || sale.pin_email_sent) && (
                    <div data-testid="pin-flags">
                      <label className="text-xs font-medium uppercase tracking-wide text-slate-500">PIN 2FA</label>
                      <div className="mt-1 flex gap-1">
                        {sale.pin_sms_sent && <Badge variant="outline" className="text-[10px]">SMS</Badge>}
                        {sale.pin_email_sent && <Badge variant="outline" className="text-[10px]">Email</Badge>}
                      </div>
                    </div>
                  )}
                  {sale.legacy_pod_warning && (
                    <div className="col-span-2" data-testid="legacy-pod-warning">
                      <label className="text-xs font-medium uppercase tracking-wide text-amber-600">⚠️ Pod folosit (legacy)</label>
                      <p className="mt-1 whitespace-pre-line text-sm text-amber-900">{sale.legacy_pod_warning}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="documents" className="mt-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Documente</CardTitle>
                    <CardDescription>Documente atașate vânzării</CardDescription>
                  </div>
                  <Button onClick={() => setUploadDialog(true)} data-testid="upload-doc-btn">
                    <Upload className="h-4 w-4 mr-2" />
                    Încarcă Document
                  </Button>
                </CardHeader>
                <CardContent>
                  {!sale.documents || sale.documents.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>Nu există documente atașate</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {sale.documents.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                              <FileText className="h-5 w-5 text-slate-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{doc.filename}</p>
                              <p className="text-sm text-slate-500">{doc.doc_type}</p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(`${API_URL}/documents/${doc.id}/download`, "_blank")}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="history" className="mt-6 space-y-6">
              {/* Iter34b — Legacy free-text fields (Obs + Exceptie Enel) */}
              {(sale.legacy_obs_text || sale.legacy_exception_text) && (
                <Card data-testid="legacy-texts-card">
                  <CardHeader>
                    <CardTitle className="text-base">Note istorice (Legacy CRM)</CardTitle>
                    <CardDescription>
                      Câmpuri free-text preluate din importul CRM Legacy (text TRANSFER/DOAR VAS, excepții Enel Easy).
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {sale.legacy_obs_text && (
                      <div data-testid="legacy-obs-text">
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          Observații (Obs)
                        </label>
                        <p className="mt-1 whitespace-pre-line text-sm text-slate-800">
                          {sale.legacy_obs_text}
                        </p>
                      </div>
                    )}
                    {sale.legacy_exception_text && (
                      <div data-testid="legacy-exception-text">
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          Excepție Enel Easy
                        </label>
                        <p className="mt-1 whitespace-pre-line text-sm text-slate-800">
                          {sale.legacy_exception_text}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              <StatusTrail contractId={sale.contract_id || sale.contract?.id} />

              {/* Iter26 — Contract documents (PDF/scan metadata) */}
              <ContractDocuments
                contractId={sale.contract_id || sale.contract?.id}
                canEdit={["admin", "backoffice"].includes(user?.role)}
              />
              
              <Card>
                <CardHeader>
                  <CardTitle>Istoric Status</CardTitle>
                  <CardDescription>Modificări ale statusului vânzării</CardDescription>
                </CardHeader>
                <CardContent>
                  {!sale.status_history || sale.status_history.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <History className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>Nu există istoric</p>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="absolute left-4 top-0 bottom-0 w-px bg-slate-200"></div>
                      <div className="space-y-6">
                        {sale.status_history.map((entry, index) => (
                          <div key={index} className="relative pl-10">
                            <div className="absolute left-0 w-8 h-8 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center">
                              {getStatusIcon(entry.new_status)}
                            </div>
                            <div className="bg-slate-50 rounded-lg p-4">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={`${getStatusColor(entry.new_status)} border`}>
                                  {entry.new_status}
                                </Badge>
                                {entry.old_status && (
                                  <>
                                    <span className="text-slate-400">←</span>
                                    <Badge variant="outline">{entry.old_status}</Badge>
                                  </>
                                )}
                              </div>
                              <p className="text-xs text-slate-400 mt-1">
                                {entry.timestamp ? new Date(entry.timestamp).toLocaleString("ro-RO") : "-"}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Iter51 Etapa 13 — Activitate (audit + field history + status changes) */}
            <TabsContent value="activity" className="mt-6">
              {activeTab === "activity" && <ActivityFeed entityType="sale" entityId={id} />}
            </TabsContent>

            {/* Iter43.3 — VIEW closure: 10 Audit & Ownership fields */}
            <TabsContent value="audit" className="mt-6 space-y-6">
              <Card data-testid="audit-ownership-card">
                <CardHeader>
                  <CardTitle className="text-base">Identificatori sistem</CardTitle>
                  <CardDescription>
                    Identificatori interni și externi ai vânzării. Setați automat de sistem; nu pot fi modificați manual.
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div data-testid="audit-sf-id">
                    <label className="text-xs font-medium uppercase tracking-wide text-slate-500">ID Salesforce</label>
                    <p className="mt-1 font-mono text-sm text-slate-800">
                      {sale.sf_id ? <CopyableValue value={sale.sf_id} mono label="ID Salesforce" /> : <EmptyValue reason="fromImport" />}
                    </p>
                  </div>
                  <div data-testid="audit-sf-agent-ref-id">
                    <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Agent SF (ref ID)</label>
                    <p className="mt-1 font-mono text-sm text-slate-800">
                      {sale.sf_agent_ref_id ? <CopyableValue value={sale.sf_agent_ref_id} mono label="Agent SF ref ID" /> : <EmptyValue reason="fromImport" />}
                    </p>
                  </div>
                  <div data-testid="audit-commercial-agent-id">
                    <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Agent comercial intern</label>
                    <p className="mt-1 font-mono text-sm text-slate-800">
                      {sale.commercial_agent_id ? <CopyableValue value={sale.commercial_agent_id} mono label="Agent intern" /> : <EmptyValue />}
                    </p>
                  </div>
                  <div data-testid="audit-raw-agent-full-name">
                    <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Agent în sursă (raw)</label>
                    <p className="mt-1 text-sm text-slate-800">
                      {sale.raw_agent_full_name || <EmptyValue reason="fromImport" />}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="audit-import-card">
                <CardHeader>
                  <CardTitle className="text-base">Sursa & importul</CardTitle>
                  <CardDescription>
                    Informații despre originea înregistrării și calitatea match-ului la import.
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div data-testid="audit-import-source">
                    <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Sursă import</label>
                    <p className="mt-1 text-sm text-slate-800">
                      {sale.import_source ? (
                        <Badge variant="outline" className="font-mono">{sale.import_source}</Badge>
                      ) : <EmptyValue />}
                    </p>
                  </div>
                  <div data-testid="audit-import-batch-id">
                    <label className="text-xs font-medium uppercase tracking-wide text-slate-500">ID batch import</label>
                    <p className="mt-1 font-mono text-xs text-slate-700 break-all">
                      {sale.import_batch_id ? <CopyableValue value={sale.import_batch_id} mono label="ID batch" /> : <EmptyValue />}
                    </p>
                  </div>
                  <div data-testid="audit-reconciliation-status">
                    <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Status reconciliere</label>
                    <p className="mt-1 text-sm text-slate-800">
                      {sale.reconciliation_status ? (
                        <Badge variant="outline">{sale.reconciliation_status}</Badge>
                      ) : "—"}
                    </p>
                  </div>
                  <div data-testid="audit-agent-match-confidence">
                    <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Confidence match agent</label>
                    <p className="mt-1 text-sm text-slate-800">
                      {sale.agent_match_confidence ? (
                        <Badge
                          className={
                            sale.agent_match_confidence === "high" ? "bg-emerald-100 text-emerald-900" :
                            sale.agent_match_confidence === "medium" ? "bg-amber-100 text-amber-900" :
                            "bg-rose-100 text-rose-900"
                          }
                        >
                          {sale.agent_match_confidence}
                        </Badge>
                      ) : "—"}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="audit-ownership-source-card">
                <CardHeader>
                  <CardTitle className="text-base">Ownership (autoritate)</CardTitle>
                  <CardDescription>
                    Sursa și flagurile care guvernează RBAC + comisionare pentru această vânzare.
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div data-testid="audit-ownership-source-of-truth">
                    <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Sursă ownership</label>
                    <p className="mt-1 text-sm text-slate-800">
                      {sale.ownership_source_of_truth ? (
                        <Badge variant="outline" className="font-mono">{sale.ownership_source_of_truth}</Badge>
                      ) : "—"}
                    </p>
                  </div>
                  <div data-testid="audit-ownership-override-flag">
                    <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Override manual</label>
                    <p className="mt-1 text-sm text-slate-800">
                      {sale.ownership_override_flag ? (
                        <Badge className="bg-amber-100 text-amber-900">Da (override activ)</Badge>
                      ) : (
                        <Badge variant="outline">Nu</Badge>
                      )}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar — ascuns pe tab Overview (acel tab e full-width după mockup) și pe mobil (acces via butonul „Info") */}
        {activeTab !== "overview" && (
        <div className="hidden md:block space-y-6 min-w-0 lg:sticky lg:top-6 lg:self-start" data-testid="quick-info-sidebar">
          {/* Quick Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Informații Rapide</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center">
                  <User className="h-4 w-4 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Client</p>
                  <p className="font-medium text-slate-900">{getClientDisplay()}</p>
                </div>
              </div>
              
              {sale.agency && (
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Building2 className="h-4 w-4 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Agenție</p>
                    <p className="font-medium text-slate-900">{sale.agency.name}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center">
                  <User className="h-4 w-4 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Agent real (semnatar)</p>
                  <p className="font-medium text-slate-900" data-testid="quick-real-agent">
                    {(sale.commercial_agent?.full_name || sale.agent_name || "—").replace(/^INACTIV\s+/i, "").trim() || "—"}
                  </p>
                </div>
              </div>

              {(() => {
                const rn = (sale.commercial_agent?.full_name || sale.agent_name || "").replace(/^INACTIV\s+/i, "").trim();
                const un = (sale.raw_agent_full_name || sale.agent_name || "").replace(/^INACTIV\s+/i, "").trim();
                if (!un || un.toUpperCase() === rn.toUpperCase()) return null;
                return (
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center">
                  <User className="h-4 w-4 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Agent încărcare</p>
                  <p className="font-medium text-slate-900" data-testid="quick-upload-agent">
                    {un}
                  </p>
                </div>
              </div>
                );
              })()}
              
              {sale.contract?.product_name && (
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Package className="h-4 w-4 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Produs</p>
                    <p className="font-medium text-slate-900">{sale.contract.product_name}</p>
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center">
                  <MapPin className="h-4 w-4 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Locație</p>
                  <p className="font-medium text-slate-900">
                    {sale.client?.county || "-"}, {sale.client?.locality || "-"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Metadata</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Data CSD</span>
                <span className="text-slate-900 font-medium" data-testid="metadata-csd-date">
                  {formatRoDate(sale.contract?.csd_date) || "-"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Lună comisionare (CSD)</span>
                <span className="text-slate-900 font-medium" data-testid="metadata-commission-month">
                  {sale.commission_month || "-"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Creat la</span>
                <span className="text-slate-900">
                  {sale.created_at ? new Date(sale.created_at).toLocaleDateString("ro-RO") : "-"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-500">ID</span>
                <CopyableValue value={sale.id} mono truncate label="Sale ID" className="text-xs" />
              </div>
              {sale.contract_id && (
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Contract ID</span>
                  <CopyableValue value={sale.contract_id} mono truncate label="Contract ID" className="text-xs" />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        )}
      </div>

      {/* Iter53 P22/P33 — Mobile Tab Selector BottomSheet (extras în MobileSheets.jsx) */}
      <MobileTabSheet
        open={showTabSheet}
        onClose={() => setShowTabSheet(false)}
        tabOptions={TAB_OPTIONS}
        activeTab={activeTab}
        onTabSelect={setActiveTab}
      />

      {/* Iter53 P22/P33 — Mobile Quick Info BottomSheet (extras în MobileSheets.jsx) */}
      <MobileQuickInfoSheet
        open={showQuickInfoMobile && activeTab !== "overview"}
        onClose={() => setShowQuickInfoMobile(false)}
        sale={sale}
        clientDisplay={getClientDisplay()}
      />

      {/* Iter53 P28/P32 — Rework Resolve Dialog (component extras) */}
      <ReworkResolveDialog
        open={reworkResolveDialog}
        onOpenChange={setReworkResolveDialog}
        sale={sale}
        note={reworkResolveNote}
        onNoteChange={setReworkResolveNote}
        saving={reworkResolving}
        onConfirm={handleResolveRework}
      />

      {/* Status Change Dialog */}
      <Dialog open={statusDialog} onOpenChange={setStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schimbă Status</DialogTitle>
            <DialogDescription>
              Selectează noul status pentru contract
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger data-testid="status-select">
                <SelectValue placeholder="Selectează status" />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((status) => (
                  <SelectItem key={status.id} value={status.name}>
                    {status.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(false)}>
              Anulează
            </Button>
            <Button onClick={handleStatusChange} disabled={!newStatus} data-testid="confirm-status">
              Salvează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmare Ștergere</DialogTitle>
            <DialogDescription>
              Ești sigur că vrei să ștergi această vânzare? Această acțiune nu poate fi anulată.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>
              Anulează
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete">
              Șterge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter23/P33 — Supplier fields manual edit (extras în SupplierFieldsEditDialog.jsx) */}
      <SupplierFieldsEditDialog
        open={supplierEditDialog}
        onOpenChange={setSupplierEditDialog}
        sale={sale}
        form={supplierForm}
        setForm={setSupplierForm}
        onSaved={fetchSale}
      />


      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Încarcă Document</DialogTitle>
            <DialogDescription>
              Selectează un fișier pentru a-l atașa vânzării
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              type="file"
              onChange={handleFileUpload}
              disabled={uploading}
              data-testid="file-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog(false)} disabled={uploading}>
              Anulează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P0.6 — Audit history dialog for SF-owned fields */}
      <Dialog open={showAuditDialog} onOpenChange={setShowAuditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Istoric status & surse</DialogTitle>
            <DialogDescription>
              Schimbări pe câmpurile SF-owned ale acestui contract (cele mai recente sus).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {auditEntries.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">
                Nicio modificare înregistrată.
              </p>
            ) : (
              auditEntries.map((e) => {
                const d = e.details || e.changed_fields || {};
                const isOverride = e.action === "sf_overwrote_manual";
                const isManual = e.action === "business_status_manual_set";
                return (
                  <div
                    key={e.id}
                    className={`p-3 rounded border text-sm ${
                      isOverride
                        ? "border-amber-300 bg-amber-50"
                        : isManual
                        ? "border-blue-200 bg-blue-50"
                        : "border-slate-200 bg-white"
                    }`}
                    data-testid={`audit-entry-${e.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">
                        {isOverride
                          ? "SF a suprascris manual"
                          : isManual
                          ? "Setat manual"
                          : "Schimbare status"}
                      </span>
                      <span className="text-xs text-slate-500">
                        {e.timestamp && new Date(e.timestamp).toLocaleString("ro-RO")}
                      </span>
                    </div>
                    {d.old_status && (
                      <p className="text-xs text-slate-600 mt-1">
                        <span className="font-mono">{d.old_status}</span> →{" "}
                        <span className="font-mono">{d.new_status || d.new_value}</span>
                        {d.new_source && (
                          <span className="ml-2 text-slate-500">[{d.new_source}]</span>
                        )}
                      </p>
                    )}
                    {d.previous_source && (
                      <p className="text-xs text-amber-800 mt-1">
                        sursă anterioară: <span className="font-mono">{d.previous_source}</span>
                        {d.previous_set_at && (
                          <> — setat la {new Date(d.previous_set_at).toLocaleString("ro-RO")}</>
                        )}
                      </p>
                    )}
                    {d.reason && (
                      <p className="text-xs text-slate-500 mt-1 italic">"{d.reason}"</p>
                    )}
                  </div>
                );
              })
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAuditDialog(false)}>
              Închide
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SaleDetail;
